import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Box, 
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  RecruitWithPlayer,
  OffensivePlayerPosition,
  DefensivePlayerPosition,
  PlayerPositionGroup,
} from "../../../types/RosterTypes";

interface RecruitProfileModalProps {
  open: boolean;
  onClose: () => void;
  recruit: RecruitWithPlayer | null;
  isNewRecruit?: boolean;
  onSave?: (recruitData: any) => Promise<void>;
}


export const RecruitProfileModal: React.FC<RecruitProfileModalProps> = ({ 
  open, 
  onClose, 
  recruit,
  isNewRecruit = false,
  onSave
}) => {
  // Form state - include name for new recruits
  const [formData, setFormData] = useState<any>({
    name: '',
    height: '',
    weight: '',
    gpa: '',
    grad_year: '',
    priority: '',
    application_status: '',
    financial_aid: '',
    film_link: '',
    notes: '',
    offensive_position: '',
    defensive_position: '',
    position_group: '',
    city: '',
    state: '',
    country: '',
    budget: '',
    agent: '',
    // Add other fields as needed
  });
  
  // Define position options
  const offensivePositions: OffensivePlayerPosition[] = ['OL', 'RB', 'TE', 'QB', 'WR'];
  const defensivePositions: DefensivePlayerPosition[] = ['DL', 'LB', 'DB'];
  const positionGroups: PlayerPositionGroup[] = ['SKILL', 'BIG_SKILL', 'BIG'];
  
  // Format height from inches to feet and inches
  const formatHeight = (inches: number) => {
    if (!inches) return "";
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };
  
  // Get composite rating for display
  const compositeRating = recruit?.player?.ratings?.composite_rating || 0;

  // Generate graduation year options (current year to current year + 8)
  const currentYear = new Date().getFullYear();
  const gradYearOptions = Array.from({ length: 9 }, (_, i) => (currentYear + i).toString());
  
  // Update form data when the recruit changes
  useEffect(() => {
    if (recruit && !isNewRecruit) {
      setFormData({
        name: recruit.player.name || '',
        height: recruit.player.height || '',
        weight: recruit.player.weight || '',
        gpa: recruit.player.gpa || '',
        grad_year: recruit.player.grad_year || '',
        priority: recruit.priority || '',
        application_status: recruit.application_status || '',
        financial_aid: recruit.financial_aid || '',
        film_link: recruit.film_link || '',
        notes: recruit.recruiting_notes || '',
        offensive_position: recruit.player.offensive_position || '',
        defensive_position: recruit.player.defensive_position || '',
        position_group: recruit.player.position_group || '',
        city: recruit.player.city || '',
        state: recruit.player.state || '',
        country: recruit.player.country || '',
        budget: recruit.budget || '',
        agent: recruit.agent || '',
      });
    } else {
      // Reset form for new recruit - include name field
      setFormData({
        name: '',
        height: '',
        weight: '',
        gpa: '',
        grad_year: currentYear.toString(),
        priority: 'LOW',
        application_status: 'NOT_APPLIED',
        financial_aid: 'UNKNOWN',
        film_link: '',
        notes: '',
        offensive_position: '',
        defensive_position: '',
        position_group: '',
        city: '',
        state: '',
        country: '',
        budget: '',
        agent: '',
      });
    }
  }, [recruit, isNewRecruit, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    if (onSave) {
      const recruitData: any = {
        player: {
          name: formData.name,
          height: formData.height ? Number(formData.height) : null,
          weight: formData.weight ? Number(formData.weight) : null,
          gpa: formData.gpa ? Number(formData.gpa) : null,
          grad_year: formData.grad_year,
          offensive_position: formData.offensive_position,
          defensive_position: formData.defensive_position,
          position_group: formData.position_group,
          city: formData.city,
          state: formData.state,
          country: formData.country,
        },
        priority: formData.priority,
        application_status: formData.application_status,
        financial_aid: formData.financial_aid,
        film_link: formData.film_link,
        recruiting_notes: formData.notes,
        budget: formData.budget ? Number(formData.budget) : null,
        agent: formData.agent,
      };
      
      // If editing existing recruit, include the ID
      if (recruit && !isNewRecruit) {
        recruitData.id = recruit.id;
        recruitData.player.id = recruit.player.id;
      }
      
      onSave(recruitData);
    }
    onClose();
  };

    console.log(recruit);


  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="recruit-profile-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 1200,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 24,
        p: 4,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        
        {/* Title with name */}
        <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
          {isNewRecruit ? 'Add New Recruit' : recruit?.player?.name || 'Edit Recruit'}
        </Typography>
        
        {/* Rating display without label as requested */}
        {!isNewRecruit && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Rating
              value={compositeRating}
              readOnly
              precision={0.5}
              max={5}
            />
          </Box>
        )}
        
        <Grid container spacing={2}>
          {/* PLAYER INFORMATION SECTION */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">Player Information</Typography>
          </Grid>
          
          {/* Show name field only for new recruits - on its own row */}
          {isNewRecruit && (
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                variant="outlined"
                size="small"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
          )}
          
          {/* Row 1: Height, Weight, Grad Year, GPA */}
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <TextField
                label="Height (inches)"
                variant="outlined"
                size="small"
                name="height"
                type="number"
                value={formData.height}
                onChange={handleInputChange}
                sx={{ width: '60%' }}
              />
              <Box 
                sx={{ 
                  width: '40%', 
                  height: '40px', 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: theme => theme.palette.text.primary
                }}
              >
                {formData.height ? formatHeight(Number(formData.height)) : "—"}
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="Weight (lbs)"
              fullWidth
              variant="outlined"
              size="small"
              name="weight"
              type="number"
              value={formData.weight}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="Grad Year"
              fullWidth
              variant="outlined"
              size="small"
              name="grad_year"
              select
              value={formData.grad_year}
              onChange={handleSelectChange}
            >
              {gradYearOptions.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="GPA"
              fullWidth
              variant="outlined"
              size="small"
              name="gpa"
              type="number"
              inputProps={{ min: 0, max: 4, step: 0.1 }}
              value={formData.gpa}
              onChange={handleInputChange}
            />
          </Grid>
          
          {/* Row 2: City, State, Country */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              fullWidth
              variant="outlined"
              size="small"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              fullWidth
              variant="outlined"
              size="small"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              label="Country"
              fullWidth
              variant="outlined"
              size="small"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
            />
          </Grid>
          
          {/* Row 3: Moved Football Information fields here */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Offensive Position</InputLabel>
              <Select
                label="Offensive Position"
                name="offensive_position"
                value={formData.offensive_position}
                onChange={handleSelectChange}
              >
                <MenuItem value="">None</MenuItem>
                {offensivePositions.map((position) => (
                  <MenuItem key={position} value={position}>{position}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Defensive Position</InputLabel>
              <Select
                label="Defensive Position"
                name="defensive_position"
                value={formData.defensive_position}
                onChange={handleSelectChange}
              >
                <MenuItem value="">None</MenuItem>
                {defensivePositions.map((position) => (
                  <MenuItem key={position} value={position}>{position}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Position Group</InputLabel>
              <Select
                label="Position Group"
                name="position_group"
                value={formData.position_group}
                onChange={handleSelectChange}
              >
                <MenuItem value="">None</MenuItem>
                {positionGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group === 'SKILL' ? 'Skill' : 
                     group === 'BIG_SKILL' ? 'Big Skill' : 
                     group === 'BIG' ? 'Big' : group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* RECRUITING INFORMATION SECTION - changed to Recruit Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Recruit Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                name="priority"
                value={formData.priority}
                onChange={handleSelectChange}
              >
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Application Status</InputLabel>
              <Select
                label="Application Status"
                name="application_status"
                value={formData.application_status}
                onChange={handleSelectChange}
              >
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="INQUIRY_SUBMITTED">Inquiry Submitted</MenuItem>
                <MenuItem value="NOT_APPLIED">Not Applied</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial Aid</InputLabel>
              <Select
                label="Financial Aid"
                name="financial_aid"
                value={formData.financial_aid}
                onChange={handleSelectChange}
              >
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="UNKNOWN">Unknown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="Budget"
              fullWidth
              variant="outlined"
              size="small"
              name="budget"
              type="number"
              value={formData.budget}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Agent"
              fullWidth
              variant="outlined"
              size="small"
              name="agent"
              value={formData.agent}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Film Link"
              fullWidth
              variant="outlined"
              size="small"
              name="film_link"
              value={formData.film_link}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Notes"
              fullWidth
              variant="outlined"
              size="small"
              name="notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
            >
              {isNewRecruit ? 'Add Recruit' : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}; 